import { ref, computed, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import type { VoiceAiClient, VoiceAiClientOptions } from '@pleased-npm/voice-ai-client';
import { CallStatus } from './callStatus';
import * as PleasedApi from '@/common/api/api.pleased';
import { logEvent, logger } from '@/common/api/logger';
import { VOICE_AI_CONFIG } from '@/common/voice-ai/voiceAi.config';
import { VoiceAiMessageKind, VoiceAiRole } from '@/common/voice-ai/voiceAiMessage';
import { useConfig } from '@/stores/useConfig';
import { devLog } from '@/common/debug';
import { parseErrorMessage } from '@/common/error';

export { CallStatus };

const ensureClientNameMeta = (clientName: string) => {
  let meta = document.querySelector('meta[name="client-name"]');

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'client-name');
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', clientName);
};

/**
 * Landing Voice AI call lifecycle (@pleased-npm/voice-ai-client).
 * Separate from Twilio — see useTwilioVoiceCallManager.
 */
export const useLandingAiVoiceCallManager = ({
  onDisconnect,
  onError,
  onMessage,
}: {
  onDisconnect?: (data: { status: CallStatus; handoffReason: string | null; aiSessionId: string | null }) => void;
  onError?: (error: unknown) => void;
  onMessage?: VoiceAiClientOptions['onMessage'];
} = {}) => {
  const configStore = useConfig();
  const { config } = storeToRefs(configStore);

  const voiceCallTime = ref(0);
  const currentStatus = ref<CallStatus>(CallStatus.CONNECTING);
  const isActive = ref(false);
  const isStarting = ref(false);
  const isMuted = ref(false);

  let voiceClient: VoiceAiClient | null = null;
  let voiceCallTimerId = 0;
  let handoffReason: string | null = null;
  let currentAiSessionId: string | null = null;

  const voiceCallDuration = computed(() => {
    const seconds = voiceCallTime.value;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  });

  const stopVoiceCallTimer = () => {
    if (voiceCallTimerId) {
      clearInterval(voiceCallTimerId);
      voiceCallTimerId = 0;
    }
    voiceCallTime.value = 0;
    currentStatus.value = CallStatus.CONNECTING;
  };

  const markConnected = () => {
    if (currentStatus.value === CallStatus.CONNECTED) return;

    currentStatus.value = CallStatus.CONNECTED;

    if (voiceCallTimerId) return;

    voiceCallTimerId = window.setInterval(() => {
      voiceCallTime.value += 1;
    }, 1000);
  };

  const finalizeSessionEnd = () => {
    if (!isActive.value && !voiceClient) {
      return;
    }

    const status = currentStatus.value;
    const reason = handoffReason;
    const aiSessionId = currentAiSessionId;

    voiceClient = null;
    isMuted.value = false;
    stopVoiceCallTimer();
    isActive.value = false;
    handoffReason = null;
    currentAiSessionId = null;
    onDisconnect?.({ status, handoffReason: reason, aiSessionId });
  };

  const handleVoiceAiError = (error: unknown) => {
    isActive.value = false;
    onError?.(error);
  };

  const start = async (audioElement: HTMLAudioElement) => {
    if (isActive.value || isStarting.value) return;

    isStarting.value = true;
    handoffReason = null;
    currentAiSessionId = null;
    stopVoiceCallTimer();
    isActive.value = true;
    currentStatus.value = CallStatus.CONNECTING;

    try {
      ensureClientNameMeta(config.value?.appName || 'SportyBet');

      const session = await PleasedApi.createVoiceAiSession();
      currentAiSessionId = session.aiSessionId;

      const { createVoiceClient } = await import('@pleased-npm/voice-ai-client');

      let lastVoiceAiLogLine = '';

      voiceClient = createVoiceClient({
        baseUrl: VOICE_AI_CONFIG.baseUrl,
        audioElement,
        jwt: session.voiceAiToken,
        aiSessionId: session.aiSessionId,

        onStatus: (label, state) => {
          if (state === 'connected') {
            devLog.debug('[VOICE-AI] status connected — marking call as active');
            markConnected();
            return;
          }

          if (state === 'error') {
            const message = lastVoiceAiLogLine || label;
            logger.error('[VOICE-AI] Status error', { label, message });
            logEvent({
              step: 'useLandingAiVoiceCallManager status error',
              label,
              message,
            });
            handleVoiceAiError(new Error(message));
            voiceClient = null;
            stopVoiceCallTimer();
            return;
          }

          if (state === 'idle') {
            devLog.log('[VOICE-AI] status idle — finalizing session');
            finalizeSessionEnd();
          }
        },
        onMessage: (text, who, metadata) => {
          if (who === VoiceAiRole.System && metadata?.kind === VoiceAiMessageKind.HandoffRequested) {
            handoffReason = metadata.reason;
            logEvent({
              step: 'useLandingAiVoiceCallManager handoff requested',
              reason: metadata.reason,
            });
          }

          onMessage?.(text, who, metadata);
        },
        onLog: (line) => {
          lastVoiceAiLogLine = line;

          if (line.startsWith('Start failed:')) {
            logger.error('[VOICE-AI] Start failed', { line });
            logEvent({
              step: 'useLandingAiVoiceCallManager start failed',
              line,
            });
            return;
          }

          devLog.debug('[VOICE-AI] Log', { line });
        },
      });

      await voiceClient.start();
    } catch (err) {
      logEvent({
        step: 'useLandingAiVoiceCallManager start failed',
        message: err instanceof Error ? err.message : String(err),
      });
      handleVoiceAiError(err);
      voiceClient = null;
      stopVoiceCallTimer();
    } finally {
      isStarting.value = false;
    }
  };

  const applyMuteState = (muted: boolean) => {
    muted ? voiceClient?.mute() : voiceClient?.unmute();
  };

  const toggleMute = () => {
    isMuted.value = !isMuted.value;
    applyMuteState(isMuted.value);
  };

  const hangUp = async () => {
    if (!voiceClient) {
      finalizeSessionEnd();
      return;
    }

    try {
      await voiceClient.stop();
    } catch (err) {
      const message = parseErrorMessage(err);
      logger.error('[VOICE-AI] Stop failed', { error: message });
      logEvent({
        step: 'useLandingAiVoiceCallManager stop failed',
        message,
      });
    } finally {
      finalizeSessionEnd();
    }
  };

  onBeforeUnmount(() => {
    if (isActive.value || voiceClient) {
      void hangUp();
      return;
    }
    stopVoiceCallTimer();
  });

  return {
    voiceCallDuration,
    isMuted,
    start,
    hangUp,
    toggleMute,
  };
};
